/**
 * RUM carte — agrégation des temps de chargement des tuiles MapLibre.
 *
 * Les Phases 1 et 2 ramènent les tuiles IGN de ~2 000 ms de moyenne à ~10 ms.
 * Rien ne signalerait une dérive : ce module fournit la mesure continue qui
 * manquait, sous la forme d'**un seul évènement agrégé par session**, envoyé
 * par la plomberie `trackEvent` existante.
 *
 * Ce fichier ne contient que de la logique pure (aucun accès au DOM hors
 * `sessionStorage`) : c'est `hooks/useRumCarte.js` qui l'alimente depuis la
 * Performance API. Les deux sont séparés pour que l'agrégation — percentiles,
 * ratios, troncature, échantillonnage — soit testable sans navigateur.
 */

/** Notation pointée, comme `map.click` ou `layer.switch`. */
export const TYPE_EVENEMENT_RUM = 'map.rum'

/**
 * Seuil sous lequel une tuile est comptée comme « rapide ».
 *
 * ATTENTION : ce n'est PAS une mesure de cache, c'est une approximation.
 * Une tuile servie par le cache HTTP du navigateur (aucun réseau) et une tuile
 * servie par le proxy-cache nginx en HIT (un aller-retour local) tombent toutes
 * deux sous ce seuil, et un réseau très rapide sur une tuile réellement en MISS
 * peut y tomber aussi. Le seuil est exporté dans l'évènement
 * (`map_tile_seuil_rapide_ms`) pour qu'un changement de valeur ne modifie pas
 * en silence la signification de la série.
 */
export const SEUIL_TUILE_RAPIDE_MS = 15

/** 10 % des sessions : assez pour voir une dérive, sans peser sur `/api/events`. */
export const TAUX_ECHANTILLONNAGE = 0.1

/**
 * Plafond de durées conservées pour le calcul des percentiles.
 *
 * Seules les durées sont plafonnées ; les compteurs (`nbRaster`,
 * `nbRasterRapides`…) restent exacts au-delà. Autrement dit une session très
 * longue dégrade la précision des percentiles mais jamais le volume de requêtes
 * ni le ratio — et `mesure_tronquee` le signale.
 */
export const MAX_DUREES = 5000

/** Décision d'échantillonnage, mémorisée pour la durée de l'onglet. */
export const CLE_ECHANTILLON = 'bonurba_rum_carte_echantillon'

/**
 * Classer une URL de ressource.
 *
 * Les deux familles de tuiles ne sont volontairement PAS mélangées :
 * - `raster` : fonds IGN, route same-origin `/tiles/ign/…` servie par le nginx
 *   du dépôt (ou l'amont `data.geopf.fr/wmts` si `VITE_IGN_TILES_BASE=direct`).
 *   C'est le chiffre de référence de la spec (211 requêtes sur une session).
 * - `vectorielle` : tuiles PBF BDNB/IRIS, route `/api/tuiles/…` proxifiée par
 *   le backend authentifié, avec sa propre zone de rate limiting nginx.
 *
 * Un p95 calculé sur l'union des deux ne voudrait rien dire : ce sont deux
 * amonts différents, deux coûts différents, deux plafonds de débit différents.
 *
 * Les fonds tiers (OpenStreetMap, OpenTopoMap, Google) ne sont pas comptés :
 * ils ne sont pas le défaut et aucun des correctifs mesurés ne les concerne.
 *
 * @returns {'raster'|'vectorielle'|null}
 */
export function classerTuile(url) {
  if (typeof url !== 'string') return null
  if (url.includes('/api/tuiles/')) return 'vectorielle'
  if (url.includes('/tiles/') || url.includes('data.geopf.fr/wmts')) return 'raster'
  return null
}

/**
 * Percentile par rang le plus proche (nearest-rank), sur un tableau non trié.
 * @returns {number|null} arrondi à la milliseconde, `null` si aucune mesure
 */
export function percentile(durees, p) {
  if (!durees?.length) return null
  const tri = [...durees].sort((a, b) => a - b)
  const rang = Math.min(tri.length, Math.max(1, Math.ceil((p / 100) * tri.length)))
  return Math.round(tri[rang - 1])
}

/** Ratio arrondi au millième, `null` si le dénominateur est nul. */
export function ratioArrondi(numerateur, total) {
  if (!total) return null
  return Math.round((numerateur / total) * 1000) / 1000
}

/** État d'accumulation d'une session de mesure. */
export function creerEtatRum() {
  return {
    dureesRaster: [],
    dureesVecteur: [],
    nbRaster: 0,
    nbVecteur: 0,
    nbRasterRapides: 0,
    nbRasterSansTransfert: 0,
    tronque: false,
  }
}

/**
 * Intégrer une entrée Resource Timing (ou toute forme équivalente
 * `{ name, duration, transferSize }`). Ignore silencieusement tout ce qui
 * n'est pas une tuile, ainsi que les durées non exploitables.
 */
export function integrerEntree(etat, entree) {
  const categorie = classerTuile(entree?.name)
  if (!categorie) return etat

  const duree = Number(entree.duration)
  if (!Number.isFinite(duree) || duree < 0) return etat

  if (categorie === 'raster') {
    etat.nbRaster += 1
    if (duree < SEUIL_TUILE_RAPIDE_MS) etat.nbRasterRapides += 1
    // `transferSize === 0` sur une entrée same-origin : servie sans réseau,
    // donc par le cache du navigateur. Contrairement au seuil des 15 ms, ce
    // signal-là ne confond pas cache navigateur et HIT du proxy nginx — il
    // permet de départager les deux dans les tableaux de bord.
    if (entree.transferSize === 0) etat.nbRasterSansTransfert += 1
    if (etat.dureesRaster.length < MAX_DUREES) etat.dureesRaster.push(duree)
    else etat.tronque = true
  } else {
    etat.nbVecteur += 1
    if (etat.dureesVecteur.length < MAX_DUREES) etat.dureesVecteur.push(duree)
    else etat.tronque = true
  }

  return etat
}

/**
 * Construire la charge utile de l'évènement `map.rum`.
 *
 * @param {object} etat état issu de `creerEtatRum` / `integrerEntree`
 * @param {object} contexte
 * @param {number|null} contexte.mapReadyMs délai montage → premier `idle`
 * @param {boolean} contexte.ongletMasque l'onglet a été masqué pendant la mesure
 * @param {string} contexte.collecte `observer` | `buffer` | `buffer_tronque`
 */
export function construireAgregat(etat, { mapReadyMs = null, ongletMasque = false, collecte = 'observer' } = {}) {
  return {
    // MapLibre suspend sa boucle `requestAnimationFrame` quand l'onglet est
    // masqué : `idle` n'arrive alors qu'au retour de l'utilisateur, et cette
    // valeur mesurerait la durée pendant laquelle il a regardé ailleurs, pas
    // la vitesse de la carte. On la neutralise plutôt que de publier un
    // nombre faux — les percentiles de tuiles, eux, restent lisibles.
    map_ready_ms: ongletMasque ? null : mapReadyMs,
    map_tiles_requested: etat.nbRaster,
    map_tile_p50_ms: percentile(etat.dureesRaster, 50),
    map_tile_p95_ms: percentile(etat.dureesRaster, 95),
    // Nom suffixé `_approx` à dessein : voir SEUIL_TUILE_RAPIDE_MS.
    map_tile_cache_hit_ratio_approx: ratioArrondi(etat.nbRasterRapides, etat.nbRaster),
    map_tile_seuil_rapide_ms: SEUIL_TUILE_RAPIDE_MS,
    map_tile_cache_navigateur_ratio: ratioArrondi(etat.nbRasterSansTransfert, etat.nbRaster),
    map_tuiles_vectorielles: etat.nbVecteur,
    map_tuile_vectorielle_p95_ms: percentile(etat.dureesVecteur, 95),
    // Drapeau de validité : une session marquée `true` doit être écartée des
    // moyennes, pas lue telle quelle.
    onglet_masque: ongletMasque,
    mesure_tronquee: etat.tronque,
    collecte,
  }
}

/** Décision d'échantillonnage, pure. */
export function decisionEchantillon(tirage, taux = TAUX_ECHANTILLONNAGE) {
  return Number.isFinite(tirage) && tirage < taux
}

/**
 * La session fait-elle partie de l'échantillon ?
 *
 * La décision est mémorisée pour la durée de l'onglet : sans cela, elle serait
 * retirée à chaque montage de la carte, et un utilisateur qui va et vient entre
 * `/app` et le reste de l'application finirait par être échantillonné bien plus
 * souvent qu'un autre — le « 10 % des sessions » deviendrait « 10 % des
 * montages de carte », biaisé vers les utilisateurs les plus actifs.
 */
export function estDansEchantillon(tirage = Math.random) {
  try {
    const memo = sessionStorage.getItem(CLE_ECHANTILLON)
    if (memo === '1') return true
    if (memo === '0') return false
  } catch {
    // Navigation privée ou stockage bloqué : on tire sans mémoriser.
  }

  const retenue = decisionEchantillon(tirage(), TAUX_ECHANTILLONNAGE)

  try {
    sessionStorage.setItem(CLE_ECHANTILLON, retenue ? '1' : '0')
  } catch {
    // Idem : l'absence de mémoire ne doit jamais empêcher la mesure.
  }

  return retenue
}
